import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { observer } from 'mobx-react';
import { reduxForm } from 'redux-form';
import { samplingDistributions } from 'mobx-store';

import * as store from 'store';

import { SAMPLING_DISTRIBUTIONS_FORM } from 'store/form';
import { getFilteredDatasetLength, getFilteredParameterValuesNotEmpty } from 'store/dataset';
import { setSamplingDistributionsCutoffs } from 'store/form/samplingDistributions';

import { makeSampleSizeValidator } from 'utilities/validators';

import SettingsFormComponent from './Component';

const SettingsFormContainer = observer(
  class SettingsFormContainer extends React.Component {
    static propTypes = {
      form: PropTypes.string.isRequired,

      datasetLength: PropTypes.number.isRequired,

      filteredDatasetLength: PropTypes.number,
      parameterType: PropTypes.string,
      parameterLevels: PropTypes.array,

      parameterSelected: PropTypes.bool.isRequired,
      parameterLevelSelected: PropTypes.bool.isRequired,

      invalid: PropTypes.bool.isRequired,
      initialize: PropTypes.func.isRequired,

      generateSamples: PropTypes.func.isRequired
    }

    static defaultProps = {
      parameterLevels: null,
      parameterType: null
    }

    constructor (props) {
      super(props);

      this.handleGenerateSamplesClick = () =>
        this.props.generateSamples();

      this.handleResetClick = () =>
        samplingDistributions.reset();
    }

    componentDidMount () {
      if (samplingDistributions.doSamplesExist) {
        return;
      }

      const { round, sqrt } = Math;
      const { datasetLength } = this.props;
      const sampleSize = round(sqrt(datasetLength));

      this.props.initialize({
        parameter: '',
        parameterLevel: '',

        alpha: 0.05,

        numberOfSamples: 100,
        sampleSize,

        tailType: 'left',

        cutoffLeft: '',
        cutoffRight: ''
      });
    }

    get optionsControlsVisible () {
      const {
        parameterType,
        parameterSelected,
        parameterLevelSelected
      } = this.props;

      if (!parameterSelected) {
        return false;
      }

      const isNumerical = parameterType === 'numerical';
      const isCategorical = parameterType === 'categorical';

      return isNumerical || (isCategorical && parameterLevelSelected);
    }

    get sampleSizeValidator () {
      return makeSampleSizeValidator(
        this.props.filteredDatasetLength > 1 ? this.props.filteredDatasetLength : 1
      );
    }

    get showEmptyDatasetWarning () {
      return this.props.filteredDatasetLength <= 0;
    }

    get generateSamplesButtonDisabled () {
      return this.props.invalid || this.showEmptyDatasetWarning;
    }

    render () {
      return (
        <SettingsFormComponent
          form={this.props.form}
          onResetClick={this.handleResetClick}
          maxSampleSize={this.props.filteredDatasetLength > 1 ? this.props.filteredDatasetLength : 1}
          parameterLevels={this.props.parameterLevels}
          sampleSizeValidator={this.sampleSizeValidator}
          samplesExist={samplingDistributions.doSamplesExist}
          optionsControlsVisible={this.optionsControlsVisible}
          showEmptyDatasetWarning={this.showEmptyDatasetWarning}
          onGenerateSamplesClick={this.handleGenerateSamplesClick}
          generateSamplesButtonDisabled={this.generateSamplesButtonDisabled}
        />
      );
    }
  }
);

const mapStateToProps = state => ({
  datasetLength: getFilteredDatasetLength(state),
  filteredDatasetLength: getFilteredParameterValuesNotEmpty(state, store.form.samplingDistributions.getParameter(state)).length,

  parameterType: store.form.samplingDistributions.getParameterType(state),
  parameterLevels: store.form.samplingDistributions.getParameterLevels(state),

  parameterSelected: store.form.samplingDistributions.isParameterSelected(state),
  parameterLevelSelected: store.form.samplingDistributions.isParameterLevelSelected(state)
});

const mapDispatchToProps = {
  generateSamples: () => (dispatch, getState) => {
    const state = getState();

    const parameterName = store.form.samplingDistributions.getParameter(state);
    const parameterType = store.form.samplingDistributions.getParameterType(state);
    const parameterLevel = store.form.samplingDistributions.getParameterLevel(state);

    const parameterValues = getFilteredParameterValuesNotEmpty(state, parameterName);

    const sampleSize = store.form.samplingDistributions.getSampleSize(state);
    const numberOfSamples = store.form.samplingDistributions.getNumberOfSamples(state);

    const parameter = { name: parameterName, type: parameterType, values: parameterValues };

    samplingDistributions.generateSamples(parameter, {
      parameterLevel, sampleSize, numberOfSamples
    });

    dispatch(setSamplingDistributionsCutoffs());
  }
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({ form: SAMPLING_DISTRIBUTIONS_FORM, destroyOnUnmount: false })(
    SettingsFormContainer
  )
);
